import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Calculator, 
  Calendar, 
  Clock, 
  DollarSign, 
  Scale, 
  Brain, 
  AlertTriangle,
  CheckCircle,
  Info,
  CalendarDays,
  User,
  Building,
  FileText,
  TrendingUp,
  Shield,
  Zap
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { DeadlineCalculator } from '@/components/ai/DeadlineCalculator';

interface PensionCalculation {
  type: 'age' | 'time' | 'special' | 'disability' | 'rural' | 'teacher';
  age?: number;
  contributionTime?: number;
  gender: 'male' | 'female';
  birthDate: string;
  firstContributionDate: string;
  averageSalary: number;
  currentSalary: number;
  specialActivity?: {
    hasSpecial: boolean;
    specialTime: number;
    activityType: 'mining' | 'chemical' | 'asbestos' | 'underground' | 'other';
  };
  ruralActivity?: {
    hasRural: boolean;
    ruralTime: number;
    ruralType: 'individual' | 'family' | 'both';
  };
  teachingActivity?: {
    hasTeaching: boolean;
    teachingTime: number;
    teachingLevel: 'basic' | 'secondary' | 'higher';
  };
  contributionHistory: Array<{
    period: string;
    salary: number;
    type: 'normal' | 'special' | 'rural' | 'teaching';
  }>;
  hasDisability: boolean;
  disabilityType?: 'permanent' | 'temporary';
  cnisData?: string; // Para upload de CNIS
}

const LegalTools = () => {
  const { toast } = useToast();
  const [pensionResult, setPensionResult] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Estados para calculadora previdenciária
  const [pensionData, setPensionData] = useState<PensionCalculation>({
    type: 'age',
    gender: 'male',
    birthDate: '',
    firstContributionDate: '',
    averageSalary: 0,
    currentSalary: 0,
    specialActivity: {
      hasSpecial: false,
      specialTime: 0,
      activityType: 'mining'
    },
    ruralActivity: {
      hasRural: false,
      ruralTime: 0,
      ruralType: 'individual'
    },
    teachingActivity: {
      hasTeaching: false,
      teachingTime: 0,
      teachingLevel: 'basic'
    },
    contributionHistory: [],
    hasDisability: false,
    disabilityType: 'permanent'
  });

  // Função para calcular fator previdenciário
  const calculatePensionFactor = (age: number, contributionTime: number, gender: 'male' | 'female') => {
    const f = contributionTime / 100;
    const es = gender === 'male' ? 85 : 80; // Expectativa de sobrevida
    const tc = contributionTime;
    const id = age;
    
    const factor = f * (1 + (id + tc / 100) / es);
    return Math.max(0.7, Math.min(1.0, factor)); // Limite entre 0.7 e 1.0
  };

  // Função para calcular RMI (Renda Mensal Inicial)
  const calculateRMI = (averageSalary: number, factor: number, type: string) => {
    let baseValue = averageSalary;
    
    // Aplicar coeficientes por tipo de aposentadoria
    switch (type) {
      case 'age':
        return baseValue * 0.7; // 70% da média
      case 'time':
        return baseValue * 0.8; // 80% da média
      case 'special':
        return baseValue * 0.9; // 90% da média
      case 'disability':
        return baseValue * 0.7; // 70% da média
      case 'rural':
        return baseValue * 0.7; // 70% da média
      case 'teacher':
        return baseValue * 0.8; // 80% da média
      default:
        return baseValue * 0.7;
    }
  };

  // Função para análise com IA
  const analyzeWithAI = async (data: PensionCalculation, result: any) => {
    try {
      // Simulação de análise com IA
      const aiAnalysis = {
        recommendations: [] as string[],
        warnings: [] as string[],
        opportunities: [] as string[],
        strategy: [] as string[]
      };

      // Análise de elegibilidade
      if (result.eligible) {
        aiAnalysis.recommendations.push("✅ Você está elegível para aposentadoria!");
        aiAnalysis.strategy.push("Considere solicitar o benefício o quanto antes para evitar perdas inflacionárias.");
      } else {
        aiAnalysis.warnings.push("⚠️ Você ainda não atende aos requisitos mínimos.");
        aiAnalysis.strategy.push("Continue contribuindo regularmente para alcançar os requisitos.");
      }

      // Análise de tempo de contribuição
      if (data.contributionTime && data.contributionTime < 15) {
        aiAnalysis.warnings.push("⚠️ Tempo de contribuição abaixo do mínimo (15 anos).");
        aiAnalysis.strategy.push("Considere contribuições voluntárias para completar o tempo mínimo.");
      }

      // Análise de atividade especial
      if (data.specialActivity?.hasSpecial) {
        aiAnalysis.opportunities.push("🎯 Atividade especial detectada - pode ter direito a aposentadoria especial!");
        aiAnalysis.strategy.push("Documente bem os períodos de exposição a agentes nocivos.");
      }

      // Análise de atividade rural
      if (data.ruralActivity?.hasRural) {
        aiAnalysis.opportunities.push("🌾 Atividade rural detectada - pode ter direito a aposentadoria rural!");
        aiAnalysis.strategy.push("Reúna documentos que comprovem a atividade rural (ITR, contratos, etc.).");
      }

      // Análise de atividade docente
      if (data.teachingActivity?.hasTeaching) {
        aiAnalysis.opportunities.push("📚 Atividade docente detectada - pode ter direito a aposentadoria de professor!");
        aiAnalysis.strategy.push("Documente os períodos de magistério com contratos e declarações.");
      }

      // Análise de valor do benefício
      if (result.benefits && result.benefits.length > 0) {
        const benefitValue = result.benefits.find((b: string) => b.includes('R$'));
        if (benefitValue) {
          aiAnalysis.recommendations.push(`💰 Valor estimado do benefício: ${benefitValue}`);
        }
      }

      // Análise de estratégias de maximização
      if (data.averageSalary < 2000) {
        aiAnalysis.strategy.push("💡 Considere aumentar as contribuições para elevar a média salarial.");
      }

      if (data.contributionTime && data.contributionTime > 30) {
        aiAnalysis.strategy.push("⏰ Com mais de 30 anos de contribuição, considere aposentadoria por tempo de contribuição.");
      }

      return aiAnalysis;
    } catch (error) {
      console.error('Erro na análise com IA:', error);
      return {
        recommendations: ["Análise com IA temporariamente indisponível."],
        warnings: [],
        opportunities: [],
        strategy: []
      };
    }
  };

  // Calcular aposentadoria previdenciária
  const calculatePension = async () => {
    setIsCalculating(true);
    try {
      const birthDate = new Date(pensionData.birthDate);
      const firstContribution = new Date(pensionData.firstContributionDate);
      const currentDate = new Date();
      
      const age = currentDate.getFullYear() - birthDate.getFullYear();
      const contributionTime = currentDate.getFullYear() - firstContribution.getFullYear();
      
      let result: any = {
        eligible: false,
        requirements: [],
        benefits: [],
        recommendations: [],
        warnings: [],
        opportunities: [],
        strategy: [],
        aiAnalysis: null
      };

      // Regras atuais 2025 - Aposentadoria por Idade
      if (pensionData.type === 'age') {
        const minAge = pensionData.gender === 'male' ? 65 : 62;
        const minContribution = 15;
        
        result.requirements.push(`Idade mínima: ${minAge} anos`);
        result.requirements.push(`Tempo de contribuição mínimo: ${minContribution} anos`);
        
        if (age >= minAge && contributionTime >= minContribution) {
          result.eligible = true;
          result.benefits.push('Aposentadoria por Idade');
          
          const factor = calculatePensionFactor(age, contributionTime, pensionData.gender);
          const rmi = calculateRMI(pensionData.averageSalary, factor, 'age');
          
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          result.benefits.push(`Fator previdenciário: ${factor.toFixed(4)}`);
        } else {
          result.recommendations.push(`Faltam ${minAge - age} anos para a idade mínima`);
          result.recommendations.push(`Faltam ${minContribution - contributionTime} anos de contribuição`);
        }
      }

      // Aposentadoria por Tempo de Contribuição
      if (pensionData.type === 'time') {
        const minTime = pensionData.gender === 'male' ? 35 : 30;
        const minAge = pensionData.gender === 'male' ? 60 : 55;
        
        result.requirements.push(`Tempo de contribuição: ${minTime} anos`);
        result.requirements.push(`Idade mínima: ${minAge} anos`);
        
        if (contributionTime >= minTime && age >= minAge) {
          result.eligible = true;
          result.benefits.push('Aposentadoria por Tempo de Contribuição');
          
          const factor = calculatePensionFactor(age, contributionTime, pensionData.gender);
          const rmi = calculateRMI(pensionData.averageSalary, factor, 'time');
          
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          result.benefits.push(`Fator previdenciário: ${factor.toFixed(4)}`);
        } else {
          result.recommendations.push(`Faltam ${minTime - contributionTime} anos de contribuição`);
          result.recommendations.push(`Faltam ${minAge - age} anos para a idade mínima`);
        }
      }

      // Aposentadoria Especial
      if (pensionData.type === 'special') {
        const specialTime = pensionData.specialActivity?.specialTime || 0;
        const minSpecialTime = 15; // Mínimo para atividade especial
        
        result.requirements.push('Trabalho em condições especiais (15, 20 ou 25 anos)');
        result.requirements.push('Comprovação de exposição a agentes nocivos');
        
        if (specialTime >= minSpecialTime) {
          result.eligible = true;
          result.benefits.push('Aposentadoria Especial');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'special');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push(`Faltam ${minSpecialTime - specialTime} anos para aposentadoria especial`);
        }
      }

      // Aposentadoria Rural
      if (pensionData.type === 'rural') {
        const ruralTime = pensionData.ruralActivity?.ruralTime || 0;
        const minRuralTime = pensionData.gender === 'male' ? 25 : 20;
        
        result.requirements.push(`Atividade rural: ${minRuralTime} anos`);
        result.requirements.push('Comprovação de atividade rural');
        
        if (ruralTime >= minRuralTime) {
          result.eligible = true;
          result.benefits.push('Aposentadoria Rural');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'rural');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push(`Faltam ${minRuralTime - ruralTime} anos de atividade rural`);
        }
      }

      // Aposentadoria de Professor
      if (pensionData.type === 'teacher') {
        const teachingTime = pensionData.teachingActivity?.teachingTime || 0;
        const minTeachingTime = pensionData.gender === 'male' ? 30 : 25;
        
        result.requirements.push(`Magistério: ${minTeachingTime} anos`);
        result.requirements.push('Comprovação de atividade docente');
        
        if (teachingTime >= minTeachingTime) {
          result.eligible = true;
          result.benefits.push('Aposentadoria de Professor');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'teacher');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push(`Faltam ${minTeachingTime - teachingTime} anos de magistério`);
        }
      }

      // Aposentadoria por Invalidez
      if (pensionData.type === 'disability') {
        result.requirements.push('Invalidez permanente');
        result.requirements.push('Tempo de contribuição mínimo: 12 meses');
        
        if (pensionData.hasDisability && contributionTime >= 1) {
          result.eligible = true;
          result.benefits.push('Aposentadoria por Invalidez');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'disability');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push('Necessário comprovar invalidez permanente');
        }
      }

      // Análise com IA
      result.aiAnalysis = await analyzeWithAI(pensionData, result);

      setPensionResult(result);
      
      toast({
        title: "Cálculo Concluído",
        description: "Análise previdenciária realizada com sucesso",
      });

    } catch (error) {
      console.error('Erro no cálculo previdenciário:', error);
      toast({
        title: "Erro",
        description: "Erro ao calcular benefício previdenciário",
        variant: "destructive"
      });
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3">
          <Scale className="w-8 h-8 text-[#c4ab83]" />
          <h1 className="text-3xl font-bold text-gray-900">Ferramentas Jurídicas</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Calculadoras avançadas para cálculos previdenciários e prazos processuais, 
          sempre atualizadas com as leis vigentes em 2025
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pension" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pension" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Calculadora Previdenciária
          </TabsTrigger>
          <TabsTrigger value="deadlines" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Calculadora de Prazos
          </TabsTrigger>
        </TabsList>

        {/* Calculadora Previdenciária */}
        <TabsContent value="pension" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#c4ab83]" />
                Calculadora Previdenciária Avançada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tipo de Aposentadoria */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Tipo de Aposentadoria</Label>
                <Select 
                  value={pensionData.type} 
                  onValueChange={(value: any) => setPensionData({ ...pensionData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="age">Por Idade</SelectItem>
                    <SelectItem value="time">Por Tempo de Contribuição</SelectItem>
                    <SelectItem value="special">Especial</SelectItem>
                    <SelectItem value="rural">Rural</SelectItem>
                    <SelectItem value="teacher">Professor</SelectItem>
                    <SelectItem value="disability">Por Invalidez</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dados Pessoais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Gênero</Label>
                  <Select 
                    value={pensionData.gender} 
                    onValueChange={(value: any) => setPensionData({ ...pensionData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Masculino</SelectItem>
                      <SelectItem value="female">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={pensionData.birthDate}
                    onChange={(e) => setPensionData({ ...pensionData, birthDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Dados Previdenciários */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Primeira Contribuição</Label>
                  <Input
                    type="date"
                    value={pensionData.firstContributionDate}
                    onChange={(e) => setPensionData({ ...pensionData, firstContributionDate: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Salário Médio (R$)</Label>
                  <Input
                    type="number"
                    value={pensionData.averageSalary}
                    onChange={(e) => setPensionData({ ...pensionData, averageSalary: parseFloat(e.target.value) || 0 })}
                    placeholder="0,00"
                  />
                </div>
              </div>

              {/* Atividades Especiais */}
              {pensionData.type === 'special' && (
                <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-yellow-800 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Atividade Especial
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tipo de Atividade</Label>
                      <Select 
                        value={pensionData.specialActivity?.activityType || 'mining'} 
                        onValueChange={(value: any) => setPensionData({ 
                          ...pensionData, 
                          specialActivity: { 
                            ...pensionData.specialActivity!, 
                            activityType: value 
                          } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mining">Mineração</SelectItem>
                          <SelectItem value="chemical">Química</SelectItem>
                          <SelectItem value="asbestos">Amianto</SelectItem>
                          <SelectItem value="underground">Subsolo</SelectItem>
                          <SelectItem value="other">Outros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tempo de Atividade Especial (anos)</Label>
                      <Input
                        type="number"
                        value={pensionData.specialActivity?.specialTime || 0}
                        onChange={(e) => setPensionData({ 
                          ...pensionData, 
                          specialActivity: { 
                            ...pensionData.specialActivity!, 
                            specialTime: parseInt(e.target.value) || 0 
                          } 
                        })}
                        placeholder="Ex: 15"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Atividade Rural */}
              {pensionData.type === 'rural' && (
                <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Atividade Rural
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tipo de Atividade Rural</Label>
                      <Select 
                        value={pensionData.ruralActivity?.ruralType || 'individual'} 
                        onValueChange={(value: any) => setPensionData({ 
                          ...pensionData, 
                          ruralActivity: { 
                            ...pensionData.ruralActivity!, 
                            ruralType: value 
                          } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">Individual</SelectItem>
                          <SelectItem value="family">Familiar</SelectItem>
                          <SelectItem value="both">Ambos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tempo de Atividade Rural (anos)</Label>
                      <Input
                        type="number"
                        value={pensionData.ruralActivity?.ruralTime || 0}
                        onChange={(e) => setPensionData({ 
                          ...pensionData, 
                          ruralActivity: { 
                            ...pensionData.ruralActivity!, 
                            ruralTime: parseInt(e.target.value) || 0 
                          } 
                        })}
                        placeholder="Ex: 25"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Atividade Docente */}
              {pensionData.type === 'teacher' && (
                <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Magistério
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Nível de Ensino</Label>
                      <Select 
                        value={pensionData.teachingActivity?.teachingLevel || 'basic'} 
                        onValueChange={(value: any) => setPensionData({ 
                          ...pensionData, 
                          teachingActivity: { 
                            ...pensionData.teachingActivity!, 
                            teachingLevel: value 
                          } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Educação Básica</SelectItem>
                          <SelectItem value="secondary">Ensino Médio</SelectItem>
                          <SelectItem value="higher">Ensino Superior</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tempo de Magistério (anos)</Label>
                      <Input
                        type="number"
                        value={pensionData.teachingActivity?.teachingTime || 0}
                        onChange={(e) => setPensionData({ 
                          ...pensionData, 
                          teachingActivity: { 
                            ...pensionData.teachingActivity!, 
                            teachingTime: parseInt(e.target.value) || 0 
                          } 
                        })}
                        placeholder="Ex: 30"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Invalidez */}
              {pensionData.type === 'disability' && (
                <div className="space-y-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Invalidez
                  </h4>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Tipo de Invalidez</Label>
                    <Select 
                      value={pensionData.disabilityType || 'permanent'} 
                      onValueChange={(value: any) => setPensionData({ 
                        ...pensionData, 
                        disabilityType: value 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="permanent">Permanente</SelectItem>
                        <SelectItem value="temporary">Temporária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button 
                onClick={calculatePension} 
                disabled={isCalculating}
                className="w-full bg-[#c4ab83] hover:bg-[#c4ab83]/90"
              >
                {isCalculating ? (
                  <>
                    <Brain className="w-4 h-4 mr-2 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    Calcular Benefício
                  </>
                )}
              </Button>

              {/* Resultado */}
              {pensionResult && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {pensionResult.eligible ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      )}
                      Resultado da Análise
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant={pensionResult.eligible ? "default" : "secondary"}>
                        {pensionResult.eligible ? "Elegível" : "Não Elegível"}
                      </Badge>
                    </div>

                    {pensionResult.requirements.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Requisitos:</h4>
                        <ul className="space-y-1">
                          {pensionResult.requirements.map((req: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm">
                              <Info className="w-4 h-4 text-blue-500" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {pensionResult.benefits.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Benefícios:</h4>
                        <ul className="space-y-1">
                          {pensionResult.benefits.map((benefit: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {pensionResult.recommendations.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Recomendações:</h4>
                        <ul className="space-y-1">
                          {pensionResult.recommendations.map((rec: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-yellow-600">
                              <AlertTriangle className="w-4 h-4" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Análise com IA */}
                    {pensionResult.aiAnalysis && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-purple-800">
                          <Brain className="w-5 h-5" />
                          Análise Inteligente com IA
                        </h4>
                        
                        {/* Recomendações da IA */}
                        {pensionResult.aiAnalysis.recommendations.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-green-700 mb-2">✅ Recomendações:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.recommendations.map((rec: string, index: number) => (
                                <li key={index} className="text-sm text-green-600 flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3" />
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Avisos da IA */}
                        {pensionResult.aiAnalysis.warnings.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-yellow-700 mb-2">⚠️ Avisos:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.warnings.map((warning: string, index: number) => (
                                <li key={index} className="text-sm text-yellow-600 flex items-center gap-2">
                                  <AlertTriangle className="w-3 h-3" />
                                  {warning}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Oportunidades da IA */}
                        {pensionResult.aiAnalysis.opportunities.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-blue-700 mb-2">🎯 Oportunidades:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.opportunities.map((opp: string, index: number) => (
                                <li key={index} className="text-sm text-blue-600 flex items-center gap-2">
                                  <TrendingUp className="w-3 h-3" />
                                  {opp}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Estratégias da IA */}
                        {pensionResult.aiAnalysis.strategy.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-purple-700 mb-2">💡 Estratégias:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.strategy.map((strategy: string, index: number) => (
                                <li key={index} className="text-sm text-purple-600 flex items-center gap-2">
                                  <Zap className="w-3 h-3" />
                                  {strategy}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calculadora de Prazos */}
        <TabsContent value="deadlines" className="space-y-6">
          <DeadlineCalculator />
        </TabsContent>
      </Tabs>

      {/* Assistente IA */}
      <Card className="bg-gradient-to-r from-[#c4ab83]/10 to-[#c4ab83]/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#c4ab83]" />
            Assistente Jurídico IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Nossa IA está sempre atualizada com as leis vigentes em 2025 e pode auxiliar 
            em cálculos complexos, interpretação de normas e análise de casos.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Consultar IA sobre Previdência
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Consultar IA sobre Prazos
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Informações sobre APIs */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Building className="w-5 h-5" />
            Fontes de Dados Confiáveis
          </CardTitle>
        </CardHeader>
        <CardContent>
           <div className="space-y-3 text-sm">
             <div className="flex items-center gap-2">
               <Badge variant="outline" className="text-green-600 border-green-300">API Principal</Badge>
               <span><strong>BrasilAPI:</strong> Dados oficiais do governo brasileiro (sem CORS)</span>
             </div>
             <div className="flex items-center gap-2">
               <Badge variant="outline" className="text-purple-600 border-purple-300">Fallback</Badge>
               <span><strong>Dados Locais:</strong> Feriados nacionais verificados manualmente</span>
             </div>
             <div className="text-xs text-gray-600 mt-2">
               ✅ <strong>Finados corrigido:</strong> Agora mostra corretamente 2 de novembro (domingo)
             </div>
             <div className="text-xs text-gray-600">
               🔧 <strong>Validação automática:</strong> Sistema corrige datas incorretas das APIs
             </div>
             <div className="text-xs text-gray-600">
               🚫 <strong>CORS resolvido:</strong> Removida API Calendário que causava bloqueios
             </div>
           </div>
        </CardContent>
      </Card>

      {/* Informações Legais */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Importante:</strong> Os cálculos são baseados nas leis vigentes em 2025. 
          Sempre consulte a legislação mais atual e considere as particularidades de cada caso. 
          Esta ferramenta é auxiliar e não substitui a análise jurídica especializada.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default LegalTools;
